// Déclaration de la solution FinOps M365 au socle (spec 133, FR-005).
//
// Ce fichier est le SEUL point de contact entre la solution et le portail. Il est découvert par
// motif (`platform/lib/solutionsLoader.ts` : `src/solutions/*/register.tsx`), donc le socle ne le
// nomme jamais. Avant le 2026-09-07, tout ce qui suit vivait dans `main.tsx` et `navigation.ts` —
// c'est-à-dire dans le socle, qui devait être modifié pour ajouter la moindre page.
//
// Une solution nouvelle se compose donc en posant `src/solutions/<code>/register.tsx` sur ce
// modèle. Aucun fichier de `platform/` n'est touché : c'est FR-006, rendu structurel.

import { lazy, Suspense, type ComponentType } from 'react'
import { registerSolution, type SectionTableauDeBordProps } from '@/platform/lib/solutionRegistry'
import { RedirectToSheet } from '@/platform/components/layout/RedirectToSheet'
import { RedirectionConservante } from '@/platform/components/layout/RedirectionConservante'
import { FILE_RETENTION_ENTRY_ID, useExpiredFilesBadge } from './hooks/useFileRetention'
import { useActionsAuditBandeau } from './hooks/useActionsAuditBandeau'

// Code-splitting des routes (US2 spec 073) : chaque page reste chargée à la demande.
function lazyPage<P extends object = object>(loader: () => Promise<Record<string, unknown>>, name: string) {
  return lazy(() => loader().then(m => ({ default: m[name] as ComponentType<P> })))
}

// Spec 161 (D-161-02, D-161-10) — la section FinOps du Tableau de bord de la plateforme ; elle remplace la page « Vue
// globale », dont l'adresse redirige vers le Tableau de bord.
const SectionFinops = lazyPage<SectionTableauDeBordProps>(() => import('./components/tableau-de-bord/SectionFinops'), 'SectionFinops')
const LicencesGlobalePage = lazyPage(() => import('./pages/LicencesGlobalePage'), 'LicencesGlobalePage')
const LicencesUsersPage = lazyPage(() => import('./pages/LicencesUsersPage'), 'LicencesUsersPage')
const RenouvellementsPage = lazyPage(() => import('./pages/RenouvellementsPage'), 'RenouvellementsPage')
const RepartitionsPage = lazyPage(() => import('./pages/RepartitionsPage'), 'RepartitionsPage')
const SimulationsPage = lazyPage(() => import('./pages/SimulationsPage'), 'SimulationsPage')
const ConformitePage = lazyPage(() => import('./pages/ConformitePage'), 'ConformitePage')
const ExportsPage = lazyPage(() => import('./pages/ExportsPage'), 'ExportsPage')
const HistoriquePage = lazyPage(() => import('./pages/HistoriquePage'), 'HistoriquePage')
const GroupsPage = lazyPage(() => import('./pages/GroupsPage'), 'GroupsPage')
const GroupDetailPage = lazyPage(() => import('./pages/GroupDetailPage'), 'GroupDetailPage')
// Spec 150 (D-150-12) — onglets « Conservation » de la fiche client et du hub Clients, chargés à la demande avec leur
// propre attente : l'onglet se charge sans masquer la fiche.
const ConservationClientTab = lazyPage<{ accountId: string }>(() => import('./pages/admin/ConservationTabs'), 'ConservationClientTab')
const ConservationHubTab = lazyPage(() => import('./pages/admin/ConservationTabs'), 'ConservationHubTab')
const tabFallback = <p className="text-body text-muted-foreground" style={{ paddingTop: 8 }}>Chargement…</p>

registerSolution({
  code: 'finops-m365',
  order: 10,

  // Les items portent désormais leur propre icône : le socle ne mappe plus leurs identifiants
  // (le `switch` de `Sidebar.tsx` ne connaît que les items du portail).
  navSection: {
    id: 'finops',
    label: 'FinOps 365',
    icon: 'bars',
    solutionCode: 'finops-m365',
    defaultOpen: true,
    // Spec 161 (D-161-07) — menu par tâches : six entrées au lieu de dix, AUCUNE page retirée. Les pages réunies
    // gardent leur adresse et deviennent des onglets de leur entrée (la plateforme affiche la barre d'onglets et couvre
    // chaque chemin). « Vue globale » quitte le menu : son contenu est la section du Tableau de bord, son adresse y
    // redirige. L'identifiant `renouvellements` est conservé : la pastille des échéances y est rattachée.
    items: [
      { id: 'licences', label: 'Licences et économies', path: '/licences', icon: 'wallet', onglets: [
        { id: 'licences', label: 'Par abonnement', path: '/licences' },
        { id: 'licences-utilisateurs', label: 'Par utilisateur', path: '/licences-utilisateurs' },
      ] },
      { id: 'renouvellements', label: 'Échéances et budget', path: '/renouvellements', icon: 'calendar' },
      { id: 'conformite', label: 'Conformité', path: '/conformite', icon: 'shield' },
      { id: 'analyses', label: 'Analyses', path: '/historique', icon: 'chart-line', onglets: [
        { id: 'historique', label: 'Historique et évolutions', path: '/historique' },
        { id: 'repartitions', label: 'Répartitions', path: '/repartitions' },
        { id: 'groupes', label: 'Groupes', path: '/groupes' },
      ] },
      { id: 'simulations', label: 'Simulations', path: '/simulations', icon: 'sliders' },
      { id: 'exports', label: 'Exports Excel', path: '/exports', icon: 'file' },
    ],
  },
  tableauDeBord: SectionFinops,
  // Spec 161, second retour (2026-10-12) — « Lancer l'audit » et « Suivre l'audit » dans le bandeau, en menu sur la date.
  useActionsBandeau: useActionsAuditBandeau,

  // Specs 148 et 150 (D-150-12) — la conservation des identités et des fichiers se lit dans l'administration des
  // clients : un onglet de la fiche client, un onglet multi-clients du hub. La plateforme les affiche sans les nommer ;
  // chaque onglet porte sa garde. Le compteur des fichiers échus s'affiche sur l'onglet du hub et sur « Clients ».
  clientTabs: [{
    id: 'finops-conservation',
    label: 'Conservation',
    render: accountId => <Suspense fallback={tabFallback}><ConservationClientTab accountId={accountId} /></Suspense>,
  }],
  clientsHubTabs: [{
    id: FILE_RETENTION_ENTRY_ID,
    label: 'Conservation',
    element: <Suspense fallback={tabFallback}><ConservationHubTab /></Suspense>,
  }],
  useAdminBadges: useExpiredFilesBadge,

  routes: [
    { path: 'dashboard', element: <RedirectionConservante vers="/overview" /> },
    { path: 'licences', element: <LicencesGlobalePage /> },
    { path: 'licences/:skuId', element: <RedirectToSheet base="/licences" param="sku" /> },
    { path: 'licences-utilisateurs', element: <LicencesUsersPage /> },
    { path: 'licences-utilisateurs/:objectId', element: <RedirectToSheet base="/licences-utilisateurs" param="user" /> },
    { path: 'renouvellements', element: <RenouvellementsPage /> },
    { path: 'repartitions', element: <RepartitionsPage /> },
    { path: 'simulations', element: <SimulationsPage /> },
    { path: 'conformite', element: <ConformitePage /> },
    { path: 'exports', element: <ExportsPage /> },
    { path: 'historique', element: <HistoriquePage /> },
    { path: 'groupes', element: <GroupsPage /> },
    { path: 'groupes/:configId', element: <GroupDetailPage /> },
  ],
})
